
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { FileText, Leaf, Droplets, TrendingUp, Package, CheckCircle } from 'lucide-react';
import ValorisationPageLayout from '@/components/valorisation/ValorisationPageLayout';
import ValorisationContentSection from '@/components/valorisation/ValorisationContentSection';

const PapiersCartons = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Recyclage Papiers et Cartons",
    "description": "Service de collecte et recyclage des papiers et cartons d'emballage. Contribution à l'économie circulaire et préservation des forêts.",
    "provider": {
      "@type": "Organization",
      "name": "Groupe Fabre Environnement",
      "url": "https://www.groupefabre-environnement.fr"
    },
    "areaServed": ["Occitanie", "Auvergne-Rhône-Alpes", "Île-de-France"],
    "serviceType": "Recyclage papiers cartons",
    "offers": {
      "@type": "Offer",
      "description": "Collecte et recyclage de tous types de papiers et cartons"
    }
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "Valorisation", href: "/valorisation" },
    { label: "Papiers Cartons", href: "/valorisation/papiers-cartons", current: true }
  ];

  const contentBlocks = [
    {
      type: 'text' as const,
      title: "Le recyclage des papiers et cartons : un enjeu environnemental majeur",
      content: "Le recyclage des papiers et cartons représente l'un des secteurs les plus matures de l'économie circulaire. Ces matériaux peuvent être recyclés jusqu'à 7 fois, permettant une réduction significative de l'impact environnemental et une préservation des ressources forestières.",
      variant: 'highlighted' as const
    },
    {
      type: 'list' as const,
      title: "Types de papiers et cartons recyclés",
      items: [
        "Cartons d'emballage et cartons ondulés",
        "Papiers de bureau : documents, impression, photocopies",
        "Journaux et magazines",
        "Papiers publicitaires et prospectus",
        "Emballages en carton : boîtes, étuis, suremballages",
        "Papiers kraft et papiers d'emballage",
        "Annuaires et catalogues",
        "Livres et cahiers (sans reliure plastique)"
      ]
    },
    {
      type: 'process' as const,
      title: "Notre processus de recyclage",
      process: [
        {
          step: 1,
          title: "Collecte sélective",
          description: "Collecte des papiers et cartons triés directement chez nos clients"
        },
        {
          step: 2,
          title: "Tri et contrôle qualité",
          description: "Séparation par type et élimination des éléments non recyclables"
        },
        {
          step: 3,
          title: "Mise en balles",
          description: "Compactage et conditionnement pour optimiser le transport"
        },
        {
          step: 4,
          title: "Transport vers papeteries",
          description: "Acheminement vers nos partenaires industriels spécialisés"
        },
        {
          step: 5,
          title: "Nouvelle vie",
          description: "Transformation en nouveaux produits papetiers"
        }
      ]
    },
    {
      type: 'benefits' as const,
      title: "Les avantages du recyclage papier-carton",
      benefits: [
        {
          title: "Préservation des forêts",
          description: "1 tonne de papier recyclé = 17 arbres préservés et réduction de la déforestation",
          icon: Leaf
        },
        {
          title: "Économie d'eau",
          description: "60% d'économie d'eau par rapport à la fabrication de papier neuf",
          icon: Droplets
        },
        {
          title: "Réduction des déchets",
          description: "Diminution du volume de déchets envoyés en décharge ou incinération",
          icon: Package
        }
      ],
      variant: 'success' as const
    },
    {
      type: 'cta' as const,
      title: "Participez à l'économie circulaire du papier",
      content: "Valorisez vos déchets papier et carton avec nos solutions de collecte adaptées à vos besoins.",
      ctaText: "Organiser une collecte",
      ctaLink: "/contact",
      variant: 'highlighted' as const
    }
  ];

  return (
    <ValorisationPageLayout
      title="Recyclage Papiers et Cartons | Groupe Fabre Environnement"
      description="Collecte et recyclage des papiers et cartons d'emballage. Solutions professionnelles pour l'économie circulaire et la préservation des forêts."
      canonical="/valorisation/papiers-cartons"
      keywords="recyclage papier, recyclage carton, économie circulaire, préservation forêts, collecte papier"
      structuredData={structuredData}
      heroTitle="Papiers & Cartons"
      heroSubtitle="Recyclage et valorisation des papiers et cartons d'emballage"
      breadcrumbItems={breadcrumbItems}
    >
      <ValorisationContentSection
        title="Recyclage des papiers et cartons"
        subtitle="Solutions complètes pour la valorisation de vos déchets papetiers"
        blocks={contentBlocks}
        className="bg-white"
      />
    </ValorisationPageLayout>
  );
};

export default PapiersCartons;
